/* 仅搜索本页配置的 FAQ；不发送搜索词到服务器，也不依赖额外搜索服务。 */
(() => {
  const normalize = (value) => String(value).normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const stopWords = new Set(['a', 'an', 'the', 'and', 'or', 'for', 'to', 'of', 'in', 'on', 'my', 'is', 'do', 'how', 'what', 'can', 'i', 'with']);
  const stem = (word) => {
    // 少量明确词形转换让 breakage / broken / breaks 能找到同一个损坏问题。
    if (/^(break|breaks|breaking|breakage|broken)$/.test(word)) return 'break';
    if (word.length > 6 && word.endsWith('ing')) return word.slice(0, -3);
    return word.length > 4 && word.endsWith('s') ? word.slice(0, -1) : word;
  };
  const words = (value) => normalize(value).split(' ').filter(Boolean).map(stem);
  const editDistance = (a, b, limit) => {
    if (Math.abs(a.length - b.length) > limit) return limit + 1;
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i++) {
      const row = [i];
      for (let j = 1; j <= b.length; j++) row[j] = Math.min(row[j - 1] + 1, previous[j] + 1, previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (Math.min(...row) > limit) return limit + 1;
      previous = row;
    }
    return previous[b.length];
  };
  const tokenScore = (query, target) => {
    if (query === target) return 1;
    if (query.length >= 3 && target.startsWith(query)) return 0.85;
    if (query.length < 4 || target.length < 4) return 0;
    const limit = query.length >= 8 ? 2 : 1;
    return editDistance(query, target, limit) <= limit ? 0.65 : 0;
  };
  const rank = (query, entry, source) => {
    const normalized = normalize(query);
    const rawTokens = words(query);
    const meaningful = rawTokens.filter((word) => !stopWords.has(word));
    const tokens = [...new Set(meaningful.length ? meaningful : rawTokens)].slice(0, 12);
    if (!tokens.length) return 0;
    let matched = 0;
    let score = 0;
    tokens.forEach((token) => {
      const question = Math.max(0, ...entry.words.map((word) => tokenScore(token, word)));
      const title = Math.max(0, ...source.words.map((word) => tokenScore(token, word)));
      if (question || title) matched++;
      score += question * 10 + title * 3;
    });
    // 多词按覆盖程度排序；允许 cleaning and care 命中只有 cleaning 的问题。
    if (matched < Math.ceil(tokens.length / 2)) return 0;
    return score + matched / tokens.length * 20 + (entry.normalized.includes(normalized) ? 60 : 0);
  };

  if (customElements.get('support-search-banner')) return;
  customElements.define('support-search-banner', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input = this.querySelector('[data-support-search-input]');
      this.results = this.querySelector('[data-support-search-results]');
      this.list = this.querySelector('[data-support-search-list]');
      this.status = this.querySelector('[data-support-search-status]');
      this.retry = this.querySelector('[data-support-search-retry]');
      this.more = this.querySelector('[data-support-search-more]');
      this.contact = this.querySelector('[data-support-search-contact]');
      this.limit = 8;
      this.entries = [];
      this.input.addEventListener('input', () => {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => { this.limit = 8; this.renderResults(); }, 180);
      }, options);
      this.querySelector('[data-support-search-form]').addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.debounce);
        this.limit = 8;
        this.renderResults();
      }, options);
      this.addEventListener('click', (event) => this.onClick(event), options);
      const update = () => { this.syncSource(); this.renderResults(); };
      document.addEventListener('support:updated', update, options);
      document.addEventListener('shopify:section:load', update, options);
      document.addEventListener('shopify:section:unload', () => queueMicrotask(update), options);
      this.syncSource();
      this.renderResults();
      customElements.whenDefined('product-support').then(() => { if (this.isConnected) update(); });
    }

    disconnectedCallback() {
      this.events.abort();
      clearTimeout(this.debounce);
    }

    syncSource() {
      this.source = [...document.querySelectorAll('product-support')].find((element) => element.initialized && typeof element.getSearchEntries === 'function');
      // 分页或主题编辑器替换分区后才重建索引，输入时只对缓存的数据做匹配。
      this.entries = this.source ? this.source.getSearchEntries().map((entry) => ({
        ...entry, normalized: normalize(entry.question), words: words(entry.question),
        sources: entry.sources.map((source) => ({ ...source, words: words(source.title) }))
      })) : [];
    }

    renderResults() {
      const query = this.input.value.trim().slice(0, 120);
      this.results.hidden = !query;
      this.querySelector('.support-banner__clear').hidden = !this.input.value;
      this.retry.hidden = true;
      this.more.hidden = true;
      this.contact.hidden = true;
      if (!query) { this.list.replaceChildren(); this.status.textContent = ''; return; }
      if (!this.source?.isConnected) {
        this.list.replaceChildren();
        this.status.textContent = 'FAQ search is unavailable on this page. Please use the contact form below.';
        this.contact.hidden = false;
        return;
      }
      const matches = this.entries.map((entry) => {
        const sources = entry.sources.map((source) => ({ ...source, score: rank(query, entry, source) })).filter((source) => source.score > 0).sort((a, b) => b.score - a.score);
        return { ...entry, sources, score: sources[0]?.score || 0 };
      }).filter((entry) => entry.score > 0).sort((a, b) => b.score - a.score || a.question.localeCompare(b.question));
      this.matches = matches;
      const focused = this.list.contains(document.activeElement) ? {
        key: document.activeElement.dataset.faqKey,
        resourceKey: document.activeElement.dataset.resourceKey
      } : null;
      this.list.replaceChildren();
      matches.slice(0, this.limit).forEach((entry, index) => {
        const item = document.createElement('li');
        item.className = 'support-search-results__item';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'support-search-results__result';
        button.dataset.supportSearchResult = index;
        button.dataset.faqKey = entry.key;
        button.dataset.resourceKey = entry.sources[0].resourceKey;
        const question = document.createElement('span');
        question.className = 'support-search-results__question';
        question.textContent = entry.question;
        const source = document.createElement('span');
        source.className = 'support-search-results__source';
        source.textContent = this.sourceLabel(entry.sources[0]);
        button.append(question, source);
        item.append(button);
        if (entry.sources.length > 1) {
          const alternatives = document.createElement('div');
          alternatives.className = 'support-search-results__sources';
          entry.sources.slice(1).forEach((source, sourceIndex) => {
            const choice = document.createElement('button');
            choice.type = 'button';
            choice.className = 'support-search-results__source-button';
            choice.dataset.supportSearchSource = sourceIndex + 1;
            choice.dataset.resultIndex = index;
            choice.dataset.faqKey = entry.key;
            choice.dataset.resourceKey = source.resourceKey;
            choice.textContent = `Also in: ${this.sourceLabel(source)}`;
            choice.setAttribute('aria-label', `View ${entry.question} in ${source.title}`);
            alternatives.append(choice);
          });
          item.append(alternatives);
        }
        this.list.append(item);
      });
      // 后续页加载会更新结果，恢复到同一问答和来源，避免键盘焦点掉回页面顶部。
      if (focused) {
        const buttons = [...this.list.querySelectorAll('button')];
        const replacement = buttons.find((button) => button.dataset.faqKey === focused.key && button.dataset.resourceKey === focused.resourceKey)
          || buttons.find((button) => button.dataset.faqKey === focused.key);
        replacement?.focus({ preventScroll: true });
      }
      const loading = this.source.loading || Boolean(this.source.nextPage);
      const countLabel = matches.length + ' matching question' + (matches.length === 1 ? '' : 's');
      this.results.setAttribute('aria-busy', String(loading && !this.source.loadFailed));
      if (this.source.loadFailed) {
        this.status.textContent = (matches.length ? countLabel + '. ' : '') + 'Some FAQs could not be loaded. Please try again to search all products.';
        this.retry.hidden = false;
      } else if (loading) {
        this.status.textContent = (matches.length ? countLabel + ' so far. ' : '') + 'Loading more FAQs…';
      } else {
        this.status.textContent = matches.length ? countLabel + '.' : 'No matching questions. Try another keyword or contact our support team.';
      }
      this.more.hidden = matches.length <= this.limit;
      this.contact.hidden = matches.length > 0 || loading || this.source.loadFailed;
    }

    sourceLabel(source) {
      return `${source.panel === 'general' ? 'General FAQs' : 'Product Support'} · ${source.title}`;
    }

    onClick(event) {
      if (event.target.closest('[data-support-search-clear]')) {
        clearTimeout(this.debounce);
        this.input.value = '';
        this.limit = 8;
        this.renderResults();
        this.input.focus({ preventScroll: true });
        return;
      }
      const result = event.target.closest('[data-support-search-result], [data-support-search-source]');
      if (result) {
        const index = result.hasAttribute('data-support-search-result') ? Number(result.dataset.supportSearchResult) : Number(result.dataset.resultIndex);
        const sourceIndex = Number(result.dataset.supportSearchSource || 0);
        const source = this.matches[index]?.sources[sourceIndex];
        if (source && !this.source?.openSearchResult(source)) {
          this.status.textContent = 'This FAQ has changed. Please search again.';
          this.syncSource();
        }
        return;
      }
      if (event.target.closest('[data-support-search-more]')) {
        const firstNewResult = this.limit;
        this.limit += 8;
        this.renderResults();
        this.list.querySelectorAll('[data-support-search-result]')[firstNewResult]?.focus({ preventScroll: true });
      }
      if (event.target.closest('[data-support-search-retry]')) this.source?.loadRemainingProducts();
      if (event.target.closest('[data-support-search-contact]')) {
        const form = document.querySelector('#ContactForm');
        form?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        form?.querySelector('input:not([type="hidden"])')?.focus({ preventScroll: true });
      }
    }
  });
})();
